
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface WaveDocument {
  id: string;
  status: string;
  total: number;
  created_at: string;
  wave_estimate_id?: string;
  wave_invoice_id?: string;
  wave_sync_status?: string;
}

interface AccountSummary {
  totalEstimates: number;
  totalInvoices: number;
  totalPaid: number;
  totalOutstanding: number;
  lastSyncAt?: string;
  syncStatus: 'synced' | 'pending' | 'error';
}

export const useWaveClientData = (clientId: string, waveCustomerId?: string) => {
  const { toast } = useToast();
  const [estimates, setEstimates] = useState<WaveDocument[]>([]);
  const [invoices, setInvoices] = useState<WaveDocument[]>([]);
  const [accountSummary, setAccountSummary] = useState<AccountSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchWaveData = useCallback(async () => {
    if (!clientId) return;

    setIsLoading(true);
    setError(null);

    try {
      // Fetch user's estimates
      const { data: estimatesData, error: estimatesError } = await supabase
        .from('estimates')
        .select('*')
        .eq('user_id', clientId)
        .order('created_at', { ascending: false });

      if (estimatesError) throw estimatesError;

      // Fetch user's invoices
      const { data: invoicesData, error: invoicesError } = await supabase
        .from('invoices')
        .select('*')
        .eq('user_id', clientId)
        .order('created_at', { ascending: false });

      if (invoicesError) throw invoicesError;

      setEstimates(estimatesData || []);
      setInvoices(invoicesData || []);

      // Calculate account summary
      const totalEstimates = estimatesData?.length || 0;
      const totalInvoices = invoicesData?.length || 0;
      const totalPaid = invoicesData?.filter(i => i.status === 'paid').reduce((sum, i) => sum + i.total, 0) || 0;
      const totalOutstanding = invoicesData?.filter(i => i.status === 'unpaid').reduce((sum, i) => sum + i.total, 0) || 0;

      // Determine sync status based on Wave sync statuses
      const allDocs = [...(estimatesData || []), ...(invoicesData || [])];
      const hasSyncErrors = allDocs.some(doc => doc.wave_sync_status === 'failed');
      const hasPendingSync = allDocs.some(doc => doc.wave_sync_status === 'pending');
      
      let syncStatus: 'synced' | 'pending' | 'error' = 'synced';
      if (hasSyncErrors) {
        syncStatus = 'error';
      } else if (hasPendingSync) {
        syncStatus = 'pending';
      }

      // Find the most recent sync timestamp
      const syncedDocs = allDocs.filter(doc => doc.wave_synced_at);
      const lastSyncAt = syncedDocs.length > 0 
        ? syncedDocs.sort((a, b) => new Date(b.wave_synced_at!).getTime() - new Date(a.wave_synced_at!).getTime())[0].wave_synced_at
        : undefined;

      setAccountSummary({
        totalEstimates,
        totalInvoices,
        totalPaid,
        totalOutstanding,
        lastSyncAt,
        syncStatus
      });

    } catch (err: any) {
      console.error('Error fetching Wave data:', err);
      setError(err.message || 'Failed to load Wave data');
      toast({
        title: 'Error Loading Data',
        description: 'Unable to load your Wave financial data. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  }, [clientId, toast]);

  const refreshData = useCallback(() => {
    fetchWaveData();
  }, [fetchWaveData]);

  useEffect(() => {
    fetchWaveData();
  }, [fetchWaveData]);

  // Set up real-time subscriptions for estimates and invoices
  useEffect(() => {
    if (!clientId) return;

    const estimatesSubscription = supabase
      .channel('estimates_changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'estimates',
        filter: `user_id=eq.${clientId}`
      }, () => {
        console.log('Estimates data changed, refreshing...');
        refreshData();
      })
      .subscribe();

    const invoicesSubscription = supabase
      .channel('invoices_changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'invoices',
        filter: `user_id=eq.${clientId}`
      }, () => {
        console.log('Invoices data changed, refreshing...');
        refreshData();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(estimatesSubscription);
      supabase.removeChannel(invoicesSubscription);
    };
  }, [clientId, refreshData]);

  return {
    estimates,
    invoices,
    accountSummary,
    isLoading,
    error,
    refreshData
  };
};
